import { useEffect, useState } from 'react';
import styled from 'styled-components';
import Button from '../../../../components/button/Button';
import { ContentFooterContainer, Footer, FooterLeft, FooterRight } from '../../../../blocks/tabs-vertical/TabsVertical';
import Typography from '../../../../components/typography/Typography';
import LoadingOverlay from '../../../../components/loading-overlay/LoadingOverlay';
import DialogViewPageHeader from '../../../../components/dialog-view-page-header/DialogViewPageHeader';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '../../../../components/table/Table';
import NoDataTableRow from '../components/NoDataTableRow';
import ConsumptionService from '../../../../services/ConsumptionService';
import QuantityCell from '../../../goods-view/components/QuantityCell';
import { ConsumableItemsDTO, ExcessItemsDTO, OrderDTO, StockDTO } from '../../../../api/api';
import NotificationService from '../../../../services/NotificationService';
import { log } from '../../../../utils/logging/log';

interface Props {
  handleReturn: () => void;
  taskId?: string;
  orders?: OrderDTO[];
  legalEntityId?: string;
  projectId?: string;
  projectLegalEntityId?: string;
}

interface ExtendedConsumableItemsDTO extends ConsumableItemsDTO {
  consumableItems?: ExtendedStockDTO[];
}

interface ExtendedStockDTO extends StockDTO {
  qty: number;
}

const ConsumeGoodsPage = (props: Props) => {
  const { handleReturn, taskId, orders, legalEntityId, projectId, projectLegalEntityId } = props;

  const [loading, setLoading] = useState<boolean>(false);
  const [goods, setGoods] = useState<ExtendedConsumableItemsDTO[]>([]);

  useEffect(() => {
    if (!orders) return;
    setLoading(true);

    const promises = orders.map((order) => order.orderId && ConsumptionService.getItemsForConsumption(order.orderId));

    Promise.all(promises)
      .then((response) => {
        setGoods(
          (response as ExtendedConsumableItemsDTO[]).map((order) => {
            return {
              ...order,
              consumableItems: order.consumableItems?.map((item) => {
                return {
                  ...item,
                  qty: item.available ?? 0
                };
              })
            };
          })
        );
      })
      .catch((e) => {
        log(e);
        NotificationService.error('Kunne ikke hente varer til forbrug');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [orders]);

  const confirm = () => {
    if (!taskId || !orders) return;
    const promises = orders.map(
      (order) =>
        order.orderId &&
        ConsumptionService.consumeItemsFromOrder(order.orderId, {
          workTaskId: parseInt(taskId),
          items: goods
            .find((g) => g.orderId === order.orderId)
            ?.consumableItems?.map((item) => {
              return {
                itemId: item.product?.itemId,
                qty: item.qty,
                itemName: item.product?.name
              };
            }),
          legalEntityId,
          projectId,
          projectLegalEntityId,
          warehouseId: orders.find((o) => o.orderId === order.orderId)?.fromWarehouseId
        })
    );

    Promise.all(promises)
      .then((response) => {
        const movedToVehicle = response.some((order) => (order as ExcessItemsDTO).movedToVehicle);

        NotificationService.success(
          `Registreringen blev gennemført. ${movedToVehicle ? 'Ubrugte varer bliver flyttet til bil.' : ''}`
        );
        handleReturn();
      })
      .catch((error) => {
        log(error);
        NotificationService.error('Der skete en fejl. Registreringen blev ikke gennemført korrekt.');
      });
  };

  return (
    <>
      <Container>
        {loading && <LoadingOverlay />}
        <DialogViewPageHeader handleReturn={handleReturn} title="Registrer forbrug" margin="20px 0px 0px 0px" />

        <div>
          <Typography fontWeight="bold" variant="h5">
            Varer fra bestillinger
          </Typography>
          <Typography>
            Du skal tage stilling til forbrug af varer fra bestillinger til opgaven. Uforbrugte varer bliver overført til din
            bil.
          </Typography>
        </div>

        <div>
          <TableContainer maxHeight="400px">
            <Table stickyHeader sx={{ height: 'max-content' }} className="TEEEEEST">
              <TableHead>
                <TableRow style={{ height: '100%' }}>
                  <TableCell sx={{ width: '20%' }}>Ordrenummer</TableCell>
                  <TableCell sx={{ width: '20%' }}>Varenummer</TableCell>
                  <TableCell sx={{ width: '30%' }}>Varenavn</TableCell>
                  <TableCell sx={{ width: '10%' }}>Antal bestilt</TableCell>
                  <TableCell sx={{ width: '20%' }}>Antal forbrugt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {goods && goods.length > 0 ? (
                  goods
                    .flatMap((order) => {
                      return order.consumableItems?.map((item) => {
                        return {
                          ...item,
                          orderId: order.orderId
                        };
                      });
                    })
                    .map((item, i) => {
                      return (
                        <TableRow key={i}>
                          <TableCell>{item?.orderId ?? '-'}</TableCell>
                          <TableCell>{item?.product?.itemId ?? '-'}</TableCell>
                          <TableCell>{item?.product?.name ?? '-'}</TableCell>
                          <TableCell>{item?.available ?? '-'}</TableCell>
                          <TableCell>
                            <QuantityCell
                              minValue={0}
                              qty={item?.qty.toString()}
                              maxAvailableStock={item?.available}
                              row={{
                                goodsId: item?.product?.itemId,
                                goodsSerial: item?.product?.serialNumber,
                                goodsBatch: item?.product?.batchNumber
                              }}
                              setGoodsAmount={(value, goodsId) => {
                                setGoods((prev) => [
                                  ...prev.map((obj) => {
                                    if (obj.orderId === item?.orderId) {
                                      return {
                                        ...obj,
                                        consumableItems: obj.consumableItems?.map((c) => {
                                          if (c.product?.itemId === goodsId) {
                                            return {
                                              ...c,
                                              qty: value
                                            };
                                          } else {
                                            return c;
                                          }
                                        })
                                      };
                                    } else {
                                      return obj;
                                    }
                                  })
                                ]);
                              }}
                            ></QuantityCell>
                          </TableCell>
                        </TableRow>
                      );
                    })
                ) : (
                  <NoDataTableRow text={'Der kunne ikke findes nogle varer'} />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </Container>
      <ContentFooterContainer>
        <Footer>
          <FooterLeft></FooterLeft>
          <FooterRight>
            <Button isLoading={loading} variant="primary" onClick={confirm}>
              Registrer vareforbrug
            </Button>
          </FooterRight>
        </Footer>
      </ContentFooterContainer>
    </>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 32px;
  position: relative;
`;

export default ConsumeGoodsPage;
